import { SprintAPI } from '../../utils/api';
import { showToast } from '../../components/Toast';
import { navigateToCampaign } from '../../utils/navigationBridge';
import { notifyInboxTargetsRestored } from '../../utils/notificationInbox';
import { relinkOrRecreateParentMission } from './relinkParentMission';
import {
  snapshotByIds,
  revertFromSnapshots,
  stripUndefinedFields,
  clearTrashFields,
  logAudit,
} from './utils';

/**
 * Restore a sprint from trash.
 * IMPORTANT: Must fetch full sprint data before updating to avoid overwriting tasks!
 */
export async function restoreSprint({ sprintId, sprints, setSprints }) {
  const sprintMetadata = sprints.find((s) => s.id === sprintId);
  if (!sprintMetadata || !sprintMetadata.deletedAt) return;

  // Descendants that were trashed together with this sprint — they restore with it.
  // (Descendants trashed individually BEFORE this sprint have no flag and stay in trash.)
  const cascadeRestoreDescendants = sprints.filter(
    (s) => s.deletedAt && s._trashedViaAncestorId === sprintId
  );
  const cascadeRestoreIds = new Set(cascadeRestoreDescendants.map((c) => c.id));

  const optimisticRestored = buildOptimisticRestored(sprintMetadata, sprints);
  const restoreSnapshots = snapshotByIds(sprints, [sprintId, ...cascadeRestoreIds]);
  notifyInboxTargetsRestored({
    campaignIds: [sprintId, ...cascadeRestoreDescendants.map((c) => c.id)],
  });
  setSprints((prev) => prev.map((s) => {
    if (s.id === sprintId) return optimisticRestored;
    if (cascadeRestoreIds.has(s.id)) return clearTrashFields(s);
    return s;
  }));

  const revertOptimisticRestore = () => revertFromSnapshots(setSprints, restoreSnapshots);

  let fullSprint;
  try {
    fullSprint = await SprintAPI.get(sprintId);
    if (!fullSprint) {
      throw new Error('Sprint not found');
    }
  } catch (error) {
    console.error('[TRASH] Error fetching sprint for restore:', error);
    revertOptimisticRestore();
    showToast(`Failed to restore "${sprintMetadata.name || 'campaign'}" — please try again`, { type: 'error' });
    return;
  }

  const originalParent = fullSprint._originalParentSprintId
    ? sprints.find((s) => s.id === fullSprint._originalParentSprintId && !s.deletedAt)
    : null;

  // Determine the task ID to use - either the stored one, or generate a new one
  // (for backwards compatibility with campaigns trashed before we stored parentTaskId)
  const taskIdToUse = fullSprint._originalParentTaskId || `task-${Date.now()}`;

  const restoredSprint = stripUndefinedFields({
    ...fullSprint,
    deletedAt: undefined,
    expiresAt: undefined,
    _trashedViaAncestorId: undefined,
    parentSprintId: originalParent ? fullSprint._originalParentSprintId : undefined,
    parentTaskId: originalParent ? taskIdToUse : undefined,
    _originalParentSprintId: undefined,
    _originalParentTaskId: undefined,
  });

  const linkedMissionId = await relinkOrRecreateParentMission({
    originalParent,
    fullSprint,
    sprintId,
    taskIdToUse,
    setSprints,
  });

  logAudit('SPRINT_RESTORED', {
    sprintId,
    sprintName: fullSprint?.name,
    restoredAsTopLevel: !originalParent,
    parentSprintId: restoredSprint.parentSprintId,
  }, sprintId);

  setSprints((prev) => prev.map((s) => (s.id === sprintId ? restoredSprint : s)));

  try {
    await SprintAPI.update(sprintId, restoredSprint);
    console.log('✅ Sprint restored:', restoredSprint.name, originalParent ? '(to original parent)' : '(as top-level)');

    await persistCascadeRestore(cascadeRestoreDescendants, setSprints);
    showRestoreToast(restoredSprint, originalParent, linkedMissionId);
  } catch (error) {
    console.error('Failed to restore sprint:', error);
    revertOptimisticRestore();
    showToast(`Failed to restore "${restoredSprint.name || 'campaign'}" — please try again`, { type: 'error' });
  }
}

function buildOptimisticRestored(sprintMetadata, sprints) {
  const optimisticParent = sprintMetadata._originalParentSprintId
    ? sprints.find((s) => s.id === sprintMetadata._originalParentSprintId && !s.deletedAt)
    : null;
  const optimisticRestored = { ...sprintMetadata };
  delete optimisticRestored.deletedAt;
  delete optimisticRestored.expiresAt;
  if (optimisticParent) {
    optimisticRestored.parentSprintId = sprintMetadata._originalParentSprintId;
  } else {
    delete optimisticRestored.parentSprintId;
    delete optimisticRestored.parentTaskId;
  }
  delete optimisticRestored._originalParentSprintId;
  delete optimisticRestored._originalParentTaskId;
  return optimisticRestored;
}

async function persistCascadeRestore(cascadeRestoreDescendants, setSprints) {
  // Fetch each in full first so persisting doesn't wipe tasks.
  for (const child of cascadeRestoreDescendants) {
    try {
      const fullChild = await SprintAPI.get(child.id);
      if (!fullChild) throw new Error('Sprint not found');
      const restoredChild = clearTrashFields(fullChild);
      setSprints((prev) => prev.map((s) => (s.id === child.id ? restoredChild : s)));
      await SprintAPI.update(child.id, restoredChild);
    } catch (error) {
      console.error('[TRASH] Failed to cascade-restore nested campaign:', child.id, error);
    }
  }
}

function showRestoreToast(restoredSprint, originalParent, linkedMissionId) {
  // Tell the user where it went — the destination isn't always visible on screen.
  // Clicking the toast navigates to WHERE the campaign landed: the parent campaign
  // with the restored mission card centered, or the campaign itself if top-level.
  const restoredName = restoredSprint.name || 'Campaign';
  if (originalParent) {
    // Pass a minimal ref (no tasks) so navigation always loads the fresh parent —
    // guaranteeing the recreated/re-linked mission node is present for the focus.
    const parentRef = { id: originalParent.id, name: originalParent.name, icon: originalParent.icon };
    showToast(`Restored "${restoredName}" into "${originalParent.name}"`, {
      type: 'success',
      onClick: () => navigateToCampaign(parentRef, linkedMissionId ? { missionId: linkedMissionId } : {}),
      actionHint: 'Click to go to it',
    });
  } else {
    showToast(`Restored "${restoredName}" as a top-level campaign`, {
      type: 'success',
      onClick: () => navigateToCampaign(restoredSprint),
      actionHint: 'Click to open campaign',
    });
  }
}
